import type { CurrencyPrice, PriceBandRow } from "app/shared/model/EditPricelist";
import { normIso } from "../currency/normalizeCurrency";

export const MAX_UNITS = 2147483647;

const toInt = (x: any, fallback: number) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : fallback;
};

export const isAnyUnits = (x: any) => toInt(x, -1) === MAX_UNITS;

export function normalizePriceBands(bands: PriceBandRow[]): PriceBandRow[] {
    const sorted = [...(bands ?? [])]
        .map((b) => ({ ...b }))
        .sort((a, b) => toInt(a.minUnits, 1) - toInt(b.minUnits, 1));

    for (const b of sorted) {
        const min = Math.max(1, toInt(b.minUnits, 1));
        const max = toInt(b.maxUnits, min);
        b.minUnits = min as any;
        b.maxUnits = Math.max(min, max) as any;
    }

    for (let i = 0; i < sorted.length - 1; i++) {
        const cur = sorted[i];
        const next = sorted[i + 1];

        const nextMin = toInt(next.minUnits, cur.minUnits);
        const maxAllowed = Math.max(cur.minUnits, nextMin - 1);

        const curMax = toInt(cur.maxUnits, cur.minUnits);
        if (isAnyUnits(curMax) || curMax > maxAllowed) {
            cur.maxUnits = maxAllowed as any;
        }
    }

    const lastIdx = sorted.length - 1;
    if (lastIdx >= 0) {
        const last = sorted[lastIdx];
        const lastMax = toInt(last.maxUnits, last.minUnits);
        last.maxUnits = (isAnyUnits(lastMax) ? MAX_UNITS : Math.max(last.minUnits, lastMax)) as any;
    }

    for (let i = 0; i < sorted.length - 1; i++) {
        if (isAnyUnits(sorted[i].maxUnits)) {
            const nextMin = toInt(sorted[i + 1].minUnits, sorted[i].minUnits);
            sorted[i].maxUnits = Math.max(sorted[i].minUnits, nextMin - 1) as any;
        }
    }

    return sorted;
}

const buildPrices = (currencyIsoCodes: string[]): CurrencyPrice[] => {
    const unique = Array.from(
        new Set(currencyIsoCodes.map((iso) => normIso(iso)).filter(Boolean))
    );
    return unique.map((currencyIsoCode) => ({
        currencyIsoCode,
        pricePerMajorUnit: 0,
        pricePerMinorUnit: 0,
    }));
};

export function insertPriceBand(
    bands: PriceBandRow[],
    insertIndex: number,
    currencyIsoCodes: string[]
): PriceBandRow[] {
    let arr = [...(bands ?? [])].map((b) => ({ ...b }));
    const idx = Math.max(0, Math.min(insertIndex, arr.length));

    const prev = idx > 0 ? arr[idx - 1] : undefined;
    const next = idx < arr.length ? arr[idx] : undefined;

    const prevMin = prev ? toInt(prev.minUnits, 1) : 0;
    const prevMax = prev ? toInt(prev.maxUnits, prevMin) : undefined;

    const newMin = prev
        ? (isAnyUnits(prevMax) ? prevMin + 1 : (prevMax as number) + 1)
        : 1;

    const newMax = next
        ? Math.max(newMin, toInt(next.minUnits, newMin) - 1)
        : MAX_UNITS;

    const newBand: any = {
        minUnits: newMin,
        maxUnits: newMax,
        prices: buildPrices(currencyIsoCodes),
        isNew: true,
    };

    if (prev && prevMax === MAX_UNITS && newMax === MAX_UNITS) {
        const updatedBand = { ...prev, maxUnits: prevMin };
        arr[idx - 1] = updatedBand;
        arr = normalizePriceBands(arr);
    }  

    const withInsert = [...arr.slice(0, idx), newBand, ...arr.slice(idx)];
    return normalizePriceBands(withInsert);
}
